import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format, addDays, subDays, isToday, isTomorrow } from "date-fns";
import { toast } from "sonner";
import {
  ClipboardList, CheckCircle2, Circle, Clock, AlertTriangle, Loader2, ArrowRight,
} from "lucide-react";
import { API } from "../../App";
import { useTimezone } from "../../contexts/TimezoneContext";

const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

function parseTimeStr(s) {
  if (!s) return null;
  const ampm = String(s).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    if (ampm[3].toUpperCase() === "PM" && h < 12) h += 12;
    if (ampm[3].toUpperCase() === "AM" && h === 12) h = 0;
    return h * 60 + parseInt(ampm[2], 10);
  }
  const h24 = String(s).trim().match(/^(\d{1,2}):(\d{2})$/);
  if (h24) return parseInt(h24[1], 10) * 60 + parseInt(h24[2], 10);
  return null;
}

function displayTime(s) {
  const mins = parseTimeStr(s);
  if (mins === null) return "";
  const h24 = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h24 % 12 || 12}:${String(m).padStart(2, "0")} ${h24 >= 12 ? "PM" : "AM"}`;
}

function localDate(dKey) {
  const [y, m, d] = String(dKey).split("-").map(Number);
  return new Date(y, m - 1, d);
}

function dateLabel(dKey) {
  const d = localDate(dKey);
  if (isToday(d)) return "Today";
  if (isTomorrow(d)) return "Tomorrow";
  return format(d, "EEE, MMM d");
}

const MAX_VISIBLE = 6;

// Upcoming dispatcher tasks (from the Jobs Timeline view) surfaced on the
// dashboard. Hidden entirely when there are no open tasks in the window.
const UpcomingTasksCard = ({ variant = "single" }) => {
  const navigate = useNavigate();
  const { getToday } = useTimezone();
  const [tasks, setTasks] = useState(null);
  const [tags, setTags] = useState([]);
  const [members, setMembers] = useState([]);
  const [togglingId, setTogglingId] = useState(null);

  const tagMap = useMemo(() => Object.fromEntries((tags || []).map((t) => [t.tag_id, t])), [tags]);
  const memberNames = useMemo(
    () => Object.fromEntries((members || []).map((m) => [m.member_id, m.name])),
    [members]
  );

  const fetchTasks = useCallback(async () => {
    try {
      const today = getToday();
      const start = format(subDays(today, 7), "yyyy-MM-dd");
      const end = format(addDays(today, 7), "yyyy-MM-dd");
      const res = await axios.get(`${API}/dispatch/tasks?start_date=${start}&end_date=${end}`, getAuthConfig());
      setTasks(Array.isArray(res.data) ? res.data : []);
    } catch { setTasks([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTasks();
    axios.get(`${API}/dispatch/tags`, getAuthConfig())
      .then((r) => setTags(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    axios.get(`${API}/team`, getAuthConfig())
      .then((r) => setMembers(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    const id = setInterval(fetchTasks, 120000);
    return () => clearInterval(id);
  }, [fetchTasks]);

  const todayKey = format(getToday(), "yyyy-MM-dd");
  const open = useMemo(() => {
    const list = (tasks || []).filter((t) => t.status !== "done");
    list.sort((a, b) =>
      a.scheduled_date === b.scheduled_date
        ? (parseTimeStr(a.scheduled_time) ?? 0) - (parseTimeStr(b.scheduled_time) ?? 0)
        : a.scheduled_date.localeCompare(b.scheduled_date)
    );
    return list;
  }, [tasks]);

  if (tasks === null || open.length === 0) return null;

  const toggleDone = async (task) => {
    setTogglingId(task.task_id);
    try {
      await axios.put(`${API}/dispatch/tasks/${task.task_id}`, { ...task, status: "done" }, getAuthConfig());
      toast.success("Task completed");
      await fetchTasks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update task");
    } finally { setTogglingId(null); }
  };

  const goTimeline = (date) =>
    navigate(`/dashboard/jobs?view=timeline${date ? `&date=${date}` : ""}`);

  const single = variant === "single";
  const visible = open.slice(0, MAX_VISIBLE);
  const extra = open.length - visible.length;
  const todayCount = open.filter((t) => t.scheduled_date === todayKey).length;

  return (
    <section
      className={`border border-border bg-card ${single ? "rounded-md" : "rounded-xl bg-card/60 backdrop-blur-sm"}`}
      data-testid="upcoming-tasks-card"
    >
      <div className="flex items-center justify-between gap-2 px-3.5 pt-3 pb-2 border-b border-border/60">
        <div className="flex items-center gap-1.5 min-w-0">
          <ClipboardList className={`w-3.5 h-3.5 ${single ? "text-amber-500 dark:text-amber-400" : "text-blue-500"}`} />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground/70">Upcoming Tasks</span>
          {todayCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-[#FFCC00] text-black text-[10px] font-bold" data-testid="upcoming-tasks-today-badge">
              {todayCount} today
            </span>
          )}
        </div>
        <button
          onClick={() => goTimeline()}
          className={`flex items-center gap-1 text-[11px] font-medium hover:underline ${single ? "text-amber-600 dark:text-amber-400" : "text-blue-500"}`}
          data-testid="upcoming-tasks-view-all"
        >
          Timeline <ArrowRight className="w-3 h-3" />
        </button>
      </div>
      <div className="px-2.5 py-2 space-y-1" data-testid="upcoming-tasks-list">
        {visible.map((task) => {
          const overdue = task.scheduled_date < todayKey;
          const taskTags = (task.tag_ids || []).map((id) => tagMap[id]).filter(Boolean);
          return (
            <div
              key={task.task_id}
              className="flex items-start gap-2 p-1.5 rounded-md hover:bg-muted/40 cursor-pointer transition-colors"
              onClick={() => goTimeline(task.scheduled_date)}
              data-testid={`upcoming-task-${task.task_id}`}
            >
              <button
                onClick={(e) => { e.stopPropagation(); toggleDone(task); }}
                className="mt-0.5 text-muted-foreground hover:text-green-500 transition-colors flex-shrink-0"
                title="Mark done"
                data-testid={`upcoming-task-toggle-${task.task_id}`}
              >
                {togglingId === task.task_id
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Circle className="w-4 h-4" />}
              </button>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium text-foreground truncate">{task.title}</div>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <span className={`text-[10px] font-semibold flex items-center gap-0.5 ${overdue ? "text-red-500" : "text-muted-foreground"}`}>
                    {overdue && <AlertTriangle className="w-2.5 h-2.5" />}
                    {overdue ? `${dateLabel(task.scheduled_date)} · overdue` : dateLabel(task.scheduled_date)}
                  </span>
                  {task.scheduled_time && (
                    <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                      <Clock className="w-2.5 h-2.5" /> {displayTime(task.scheduled_time)}
                    </span>
                  )}
                  {taskTags.map((t) => (
                    <span key={t.tag_id} className="px-1.5 rounded text-[9px] font-bold text-white" style={{ backgroundColor: t.color }}>{t.name}</span>
                  ))}
                  <span className="text-[10px] text-muted-foreground">{memberNames[task.assigned_to] || "Office"}</span>
                </div>
              </div>
              <CheckCircle2 className="w-3.5 h-3.5 text-transparent" aria-hidden />
            </div>
          );
        })}
        {extra > 0 && (
          <button
            onClick={() => goTimeline()}
            className="w-full text-center text-[11px] text-muted-foreground hover:text-foreground py-1"
            data-testid="upcoming-tasks-more"
          >
            +{extra} more task{extra !== 1 ? "s" : ""}
          </button>
        )}
      </div>
    </section>
  );
};

export default UpcomingTasksCard;
